import { c as _c } from "react/compiler-runtime";
import { postHostedIdentifySessionChallengeVerify } from '@onefootprint/fetch-hosted';
import { postHostedIdentifySessionChallengeMutation } from '@onefootprint/fetch-hosted/queries';
import { useRequestError } from '@onefootprint/request';
import type { ChallengeKind, ChallengeVerifyRequest, IdentifiedUser, UserChallengeData } from '@onefootprint/request-types';
import { Button, Divider, useToast } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { type JSX, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getLogger } from '@/idv/utils';
import type { Context } from '../identify.types';
import calculateRetryTime from '../queries/get-retry-time';
import { getDisplayEmail, getDisplayPhone } from '../utils/get-display-contact-info';
import getHeader from '../utils/get-header';
import ChallengeSna from './challenge-sna';
import PinForm from './pin-form';
import { PoweredByVerified, useMarkConsentMutation, VerifiedPrefillDisclaimer } from './verified-prefill-consent';
const {
  logWarn
} = getLogger({
  location: 'identify-challenge'
});
const IS_TEST = process.env.NODE_ENV === 'test';
const SUCCESS_EVENT_DELAY_MS = IS_TEST ? 10 : 1500;
const useChallengeState = () => {
  const $ = _c(7);
  const {
    getErrorCode,
    getErrorContext
  } = useRequestError();
  const [retryDisabledUntil, setRetryDisabledUntil] = useState(undefined);
  let t0;
  if ($[0] !== getErrorCode || $[1] !== getErrorContext) {
    const getRetryAfterS = error => {
      const errorCode = getErrorCode(error);
      const isRateLimited = errorCode === "E104";
      if (isRateLimited) {
        const context = getErrorContext(error);
        return typeof context?.seconds === "number" ? context.seconds : 0;
      }
      return 0;
    };
    t0 = (error_0, isFirstAttempt) => {
      const retryAfterS = getRetryAfterS(error_0);
      setRetryDisabledUntil(calculateRetryTime(retryAfterS));
      const isRateLimited_0 = Boolean(retryAfterS);
      if (isFirstAttempt && isRateLimited_0) {
        return;
      }
      return error_0;
    };
    $[0] = getErrorCode;
    $[1] = getErrorContext;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const handleChallengeError = t0;
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = challengeData => {
      setRetryDisabledUntil(calculateRetryTime(challengeData.timeBeforeRetryS));
    };
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleChallengeSuccess = t1;
  let t2;
  if ($[4] !== handleChallengeError || $[5] !== retryDisabledUntil) {
    t2 = {
      retryDisabledUntil,
      handleChallengeSuccess,
      handleChallengeError
    };
    $[4] = handleChallengeError;
    $[5] = retryDisabledUntil;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
type ChallengeProps = {
  children?: React.ReactNode;
  defaultValues: {
    email: string | undefined;
    phoneNumber: string | undefined;
  };
  context: Context;
  /** If this is a login challenge, the IdentifiedUser from the requirement. */
  user?: IdentifiedUser;
  onDone: (delayMs?: number) => Promise<void>;
  onPrev?: () => void;
  onError: (location: string, error: unknown) => void;
  fromChallengeSelector?: boolean;
  challengeKind: ChallengeKind;
};
const Challenge = props => {
  const $ = _c(8);
  const {
    verifiedPrefillContext
  } = props.context.state;
  const [requiresConsent, setRequiresConsent] = useState(verifiedPrefillContext?.isEnabled && verifiedPrefillContext?.requiresConsent);
  if (props.challengeKind === "sms" && requiresConsent) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = () => setRequiresConsent(false);
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    let t1;
    if ($[1] !== props) {
      t1 = <VerifiedConsent {...props} onContinue={t0} />;
      $[1] = props;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  if (props.challengeKind === "sna") {
    let t0;
    if ($[3] !== props.context || $[4] !== props.onDone) {
      t0 = <ChallengeSna context={props.context} onDone={props.onDone} onError={props.onDone} />;
      $[3] = props.context;
      $[4] = props.onDone;
      $[5] = t0;
    } else {
      t0 = $[5];
    }
    return t0;
  }
  let t0;
  if ($[6] !== props) {
    t0 = <ChallengeNonSna {...props} />;
    $[6] = props;
    $[7] = t0;
  } else {
    t0 = $[7];
  }
  return t0;
};
const VerifiedConsent = t0 => {
  const $ = _c(26);
  const {
    context,
    onPrev,
    onContinue
  } = t0;
  const {
    state: t1,
    initArgs: t2
  } = context;
  const {
    identifyToken,
    verifiedPrefillContext
  } = t1;
  const {
    logoConfig
  } = t2;
  const collectsSsn = verifiedPrefillContext?.collectsSsn ?? false;
  let t3;
  if ($[0] !== logoConfig || $[1] !== onPrev) {
    t3 = getHeader(logoConfig, onPrev);
    $[0] = logoConfig;
    $[1] = onPrev;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const Header = t3;
  const {
    t
  } = useTranslation("identify");
  const markConsentMutation = useMarkConsentMutation(identifyToken, verifiedPrefillContext);
  let t4;
  if ($[3] !== markConsentMutation || $[4] !== onContinue) {
    t4 = async () => {
      await markConsentMutation.mutateAsync();
      onContinue();
    };
    $[3] = markConsentMutation;
    $[4] = onContinue;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handleContinue = t4;
  let t5;
  if ($[6] !== t) {
    t5 = t(_temp);
    $[6] = t;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== collectsSsn) {
    t6 = <VerifiedPrefillDisclaimer collectsSsn={collectsSsn} />;
    $[8] = collectsSsn;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== Header || $[11] !== t5 || $[12] !== t6) {
    t7 = <Header title={t5} subtitle={t6} />;
    $[10] = Header;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t) {
    t8 = t(_temp2);
    $[14] = t;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== handleContinue || $[17] !== t8) {
    t9 = <Button fullWidth={true} size="large" data-dd-action-name="verified-consent:continue" onClick={handleContinue}>{t8}</Button>;
    $[16] = handleContinue;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Divider />;
    t11 = <PoweredByVerified />;
    $[19] = t10;
    $[20] = t11;
  } else {
    t10 = $[19];
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t9) {
    t12 = <div className="flex flex-col gap-6 mt-8 w-full">{t9}{t10}{t11}</div>;
    $[21] = t9;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t12 || $[24] !== t7) {
    t13 = <div className="flex flex-col items-center justify-center">{t7}{t12}</div>;
    $[23] = t12;
    $[24] = t7;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  return t13;
};
const ChallengeNonSna = t0 => {
  const $ = _c(63);
  const {
    children,
    defaultValues,
    context,
    user,
    onDone,
    onPrev,
    onError,
    challengeKind,
    fromChallengeSelector
  } = t0;
  const {
    t
  } = useTranslation("identify");
  const {
    state: t1,
    initArgs: t2
  } = context;
  const {
    identifyToken
  } = t1;
  const {
    logoConfig
  } = t2;
  const {
    getErrorCode
  } = useRequestError();
  const {
    retryDisabledUntil,
    handleChallengeError,
    handleChallengeSuccess
  } = useChallengeState();
  const headers = {
    "X-Fp-Authorization": identifyToken
  };
  const challengeMutation = useMutation({
    ...postHostedIdentifySessionChallengeMutation({
      headers
    }),
    onSuccess: data => {
      if (data.error) {
        logWarn("Error received from primary challenge vendor, possibly trying fallback", data.error);
      }
      if (challengeMutation.data) {
        toast({
          title: t(_temp3),
          description: t(_temp4)
        });
      }
      handleChallengeSuccess(data.challengeData);
    },
    onError: error => {
      const processedError = handleChallengeError(error, !challengeMutation.data);
      if (processedError) {
        onError("challenge", processedError);
      }
    }
  });
  const verifyMutation = useMutation({
    mutationFn: async body => {
      await postHostedIdentifySessionChallengeVerify({
        headers,
        body: {
          ...body
        }
      });
      await onDone(SUCCESS_EVENT_DELAY_MS);
    },
    onError: error_0 => {
      const errorCode = getErrorCode(error_0);
      const isChallengeExpired = errorCode === "E103";
      if (isChallengeExpired) {
        handleRequestChallenge();
      }
      onError("challenge-verify", error_0);
    }
  });
  const {
    toast: t3
  } = useToast();
  const toast = t3;
  const handleRequestChallenge = () => {
    challengeMutation.mutate({
      body: {
        challengeKind
      }
    });
  };
  let t4;
  if ($[0] !== verifyMutation) {
    t4 = pin => {
      verifyMutation.mutate({
        challengeResponse: pin
      });
    };
    $[0] = verifyMutation;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const handleVerifyPin = t4;
  let t5;
  if ($[2] !== challengeMutation.data || $[3] !== challengeMutation.isPending || $[4] !== handleRequestChallenge) {
    t5 = () => {
      if (!challengeMutation.data && !challengeMutation.isPending) {
        handleRequestChallenge();
      }
    };
    $[2] = challengeMutation.data;
    $[3] = challengeMutation.isPending;
    $[4] = handleRequestChallenge;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== challengeMutation.data) {
    t6 = [challengeMutation.data];
    $[6] = challengeMutation.data;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  useEffect(t5, t6);
  let headerTitle;
  let formTitle;
  if (challengeKind === "email") {
    let t7;
    if ($[8] !== defaultValues.email || $[9] !== t || $[10] !== user) {
      const displayEmail = getDisplayEmail({
        user,
        email: defaultValues.email
      });
      let t8;
      if ($[13] !== t) {
        t8 = t(_temp5);
        $[13] = t;
        $[14] = t8;
      } else {
        t8 = $[14];
      }
      headerTitle = t8;
      t7 = displayEmail ? <span data-dd-privacy="mask" data-dd-action-name="Subtitle with email">{t(_temp6, {
          email: displayEmail
        })}</span> : t(_temp7);
      $[8] = defaultValues.email;
      $[9] = t;
      $[10] = user;
      $[11] = t7;
      $[12] = headerTitle;
    } else {
      t7 = $[11];
      headerTitle = $[12];
    }
    formTitle = t7;
  } else {
    if (challengeKind === "sms") {
      let t7;
      if ($[15] !== defaultValues.phoneNumber || $[16] !== t || $[17] !== user) {
        const displayPhone = getDisplayPhone({
          user,
          phoneNumber: defaultValues.phoneNumber
        });
        let t8;
        if ($[20] !== t) {
          t8 = t(_temp8);
          $[20] = t;
          $[21] = t8;
        } else {
          t8 = $[21];
        }
        headerTitle = t8;
        t7 = displayPhone ? <span data-dd-privacy="mask" data-dd-action-name="Subtitle with phone">{t(_temp9, {
            phone: displayPhone
          })}</span> : t(_temp10);
        $[15] = defaultValues.phoneNumber;
        $[16] = t;
        $[17] = user;
        $[18] = t7;
        $[19] = headerTitle;
      } else {
        t7 = $[18];
        headerTitle = $[19];
      }
      formTitle = t7;
    }
  }
  const shouldShowWelcomeBack = !!user && !user.isUnverified && !fromChallengeSelector;
  if (shouldShowWelcomeBack) {
    let t7;
    if ($[22] !== t) {
      t7 = t(_temp11);
      $[22] = t;
      $[23] = t7;
    } else {
      t7 = $[23];
    }
    headerTitle = t7;
  }
  let t7;
  if ($[24] !== logoConfig || $[25] !== onPrev) {
    t7 = getHeader(logoConfig, onPrev);
    $[24] = logoConfig;
    $[25] = onPrev;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const Header = t7;
  let t8;
  if ($[27] !== challengeMutation.data || $[28] !== t || $[29] !== verifyMutation.isError) {
    t8 = verifyMutation.isError ? t(_temp12) : challengeMutation.data?.error ? t(_temp13) : undefined;
    $[27] = challengeMutation.data;
    $[28] = t;
    $[29] = verifyMutation.isError;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  const pinFormHint = t8;
  const t9 = formTitle as JSX.Element;
  let t10;
  if ($[31] !== Header || $[32] !== headerTitle || $[33] !== t9) {
    t10 = <Header title={headerTitle} subtitle={t9} />;
    $[31] = Header;
    $[32] = headerTitle;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  const t11 = verifyMutation.isError;
  const t12 = challengeMutation.isPending;
  const t13 = challengeMutation.isPending || verifyMutation.isPending;
  const t14 = verifyMutation.isSuccess;
  const t15 = verifyMutation.isPending;
  let t16;
  if ($[35] !== t) {
    t16 = t(_temp14);
    $[35] = t;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t) {
    t17 = t(_temp15);
    $[37] = t;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t) {
    t18 = t(_temp16);
    $[39] = t;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t) {
    t19 = t(_temp17);
    $[41] = t;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t16 || $[44] !== t17 || $[45] !== t18 || $[46] !== t19) {
    t20 = {
      resendCountDown: t16,
      resendCta: t17,
      success: t18,
      verifying: t19
    };
    $[43] = t16;
    $[44] = t17;
    $[45] = t18;
    $[46] = t19;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== challengeMutation.isPending || $[49] !== children || $[50] !== handleRequestChallenge || $[51] !== handleVerifyPin || $[52] !== pinFormHint || $[53] !== retryDisabledUntil || $[54] !== t13 || $[55] !== t20 || $[56] !== verifyMutation.isError || $[57] !== verifyMutation.isPending || $[58] !== verifyMutation.isSuccess) {
    t21 = <PinForm hasError={t11} pinFormHint={pinFormHint} isPending={t12} isResendLoading={t13} isSuccess={t14} isVerifying={t15} onComplete={handleVerifyPin} onResend={handleRequestChallenge} resendDisabledUntil={retryDisabledUntil} texts={t20} className="mt-8">{children}</PinForm>;
    $[48] = challengeMutation.isPending;
    $[49] = children;
    $[50] = handleRequestChallenge;
    $[51] = handleVerifyPin;
    $[52] = pinFormHint;
    $[53] = retryDisabledUntil;
    $[54] = t13;
    $[55] = t20;
    $[56] = verifyMutation.isError;
    $[57] = verifyMutation.isPending;
    $[58] = verifyMutation.isSuccess;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  let t22;
  if ($[60] !== t10 || $[61] !== t21) {
    t22 = <div className="flex flex-col">{t10}{t21}</div>;
    $[60] = t10;
    $[61] = t21;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  return t22;
};
export default Challenge;
function _temp($) {
  return $["pin-verification"].verifiedPrefill.autofillYourInformation;
}
function _temp2($_0) {
  return $_0.continue;
}
function _temp3($) {
  return $["pin-verification"].success;
}
function _temp4($_0) {
  return $_0["pin-verification"]["new-code-sent-description"];
}
function _temp5($_1) {
  return $_1["email-challenge"]["verify-title"];
}
function _temp6($_2) {
  return $_2["email-challenge"]["prompt-with-email"];
}
function _temp7($_3) {
  return $_3["email-challenge"]["prompt-without-email"];
}
function _temp8($_4) {
  return $_4["sms-challenge"]["verify-title"];
}
function _temp9($_5) {
  return $_5["sms-challenge"]["prompt-with-phone"];
}
function _temp10($_6) {
  return $_6["sms-challenge"]["prompt-without-phone"];
}
function _temp11($_7) {
  return $_7["sms-challenge"]["welcome-back-title"];
}
function _temp12($_8) {
  return $_8["pin-verification"]["incorrect-code"];
}
function _temp13($_9) {
  return $_9["pin-verification"]["challenge-taking-longer"];
}
function _temp14($_10) {
  return $_10["pin-verification"]["resend-countdown"];
}
function _temp15($_11) {
  return $_11["pin-verification"]["resend-cta"];
}
function _temp16($_12) {
  return $_12["pin-verification"].success;
}
function _temp17($_13) {
  return $_13["pin-verification"].verifying;
}